// ResearchTab.jsx — the Research launcher (tier-filtered grid → mounts real
// research surfaces) and CustomPane (build-your-own multi-widget dashboard).

import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, Plus, X, LayoutGrid,
  Layers, Activity, LineChart, BarChart3, Calendar,
  Landmark, Percent, BookOpen, Ship,
} from 'lucide-react';
import { CHILD_THEME } from './rrtvFormat';
import { AssetWorkbench } from './AssetDetail';

// ── Research surfaces (mounted inside the Research tab, adapted to the RRTV shell) ──
import GenomaTradeExplorer      from '../../components/GenomaTradeExplorer';
import HypothesisExplorer       from '../../components/HypothesisExplorer';
import HypothesisCorrelationLab from '../../components/HypothesisCorrelationLab';
import IndexCorrelationLab      from '../../components/IndexCorrelationLab';
import CurvaDeJuros             from '../../components/CurvaDeJuros';
import COPOMOptionsCard         from '../../components/COPOMOptionsCard';
import BitmexOrderBook          from '../../components/BitmexOrderBook';
import WeekAhead                from '../../components/Week_Ahead';
import FreeShopIndicator        from '../../components/dutyfree_indicator';
// ── StocksPage UX, reincorporated: its own building blocks (not the page) ──
import { StocksHero }           from '../../components/StocksHero';
import { MagmaNewsSection }     from '../../components/MagmaNewsSection';

const RESEARCH_TOOLS = [
  { id: 'genoma',     title: 'Genoma · Comércio',           tier: 'hard',  icon: Layers,     desc: 'Clusters de mercadorias, drilldown por componente, país de origem.', Comp: GenomaTradeExplorer,       themed: true },
  { id: 'teses',      title: 'Teses & Hipóteses',           tier: 'hard',  icon: BookOpen,   desc: 'Cadeias causais e séries temporais de tese.',                        Comp: HypothesisExplorer },
  { id: 'lab-teses',  title: 'Lab · Correlação de Teses',   tier: 'hard',  icon: Activity,   desc: 'Workbench de correlação entre teses e ativos.',                      Comp: HypothesisCorrelationLab },
  { id: 'lab-idx',    title: 'Lab · Correlação de Índices', tier: 'hard',  icon: BarChart3,  desc: 'Matriz de correlação e construção de cestas.',                       Comp: IndexCorrelationLab },
  { id: 'curva',      title: 'Curva de Juros',              tier: 'hard',  icon: LineChart,  desc: 'DI futuro — clique um vértice, veja histórico e regimes.',           Comp: CurvaDeJuros,              themed: true },
  { id: 'copom',      title: 'COPOM · Expectativas',        tier: 'mid',   icon: Percent,    desc: 'Probabilidades de decisão via opções; abra a curva.',                Comp: COPOMOptionsCard,          themed: true },
  { id: 'bitmex',     title: 'Order Book · BitMEX',         tier: 'hard',  icon: Landmark,   desc: 'Profundidade L2 ao vivo; clique um nível.',                          Comp: BitmexOrderBook,           themed: true },
  { id: 'week',       title: 'Semana à Frente',             tier: 'light', icon: Calendar,   desc: 'Agenda de eventos — clique para drilar no que se espera.',           Comp: WeekAhead, props: { imgBase: '/week_ahead_images', logoBase: '/api' } },
  { id: 'duty',       title: 'Duty Free · RR Magma',        tier: 'mid',   icon: Ship,       desc: 'Índice proprietário de fluxo — o nosso número, não o do mercado.',    Comp: FreeShopIndicator },
];

const TIER_LABEL = { light: 'JÚNIOR', mid: 'PLENO', hard: 'SÊNIOR' };

const TIER_FILTERS = [
  { id: 'all',   label: 'Todos'  },
  { id: 'light', label: 'Júnior' },
  { id: 'mid',   label: 'Pleno'  },
  { id: 'hard',  label: 'Sênior' },
];

export function ResearchTab({ onImmersive }) {
  const [openId, setOpenId] = useState(null);
  const [tier,   setTier]   = useState('all');
  const tool  = RESEARCH_TOOLS.find(t => t.id === openId);
  const shown = tier === 'all' ? RESEARCH_TOOLS : RESEARCH_TOOLS.filter(t => t.tier === tier);

  // Opening a tool = a deeper layer → collapse chrome (immersive)
  useEffect(() => { onImmersive?.(!!openId); }, [openId, onImmersive]);

  if (tool) {
    const { Comp, themed } = tool;
    const extra = tool.props || {};
    return (
      <div className="rrtv-tool-view">
        <header className="rrtv-tool-head">
          <button className="rrtv-tool-back" onClick={() => setOpenId(null)}>
            <ArrowLeft size={16} /> Research
          </button>
          <span className="rrtv-tool-title">{tool.title}</span>
          <span className={`rrtv-tier-badge ${tool.tier}`}>{TIER_LABEL[tool.tier]}</span>
        </header>
        <div className="rrtv-tool-body">
          {themed ? <Comp theme={CHILD_THEME} {...extra} /> : <Comp {...extra} />}
        </div>
      </div>
    );
  }

  return (
    <div className="rrtv-research">
      {/* Pinned hero — global 52-week leader + geopolitics (StocksHero) */}
      <StocksHero theme={CHILD_THEME} />

      <div className="rrtv-research-bar">
        <div className="rrtv-research-intro">
          <h2 className="rrtv-research-h">Research</h2>
          <p className="rrtv-research-sub">Bancada de análise. Filtre por nível e abra uma ferramenta.</p>
        </div>
        <div className="rrtv-research-tiers" role="tablist" aria-label="Nível">
          {TIER_FILTERS.map(f => (
            <button key={f.id} className={`rrtv-research-tier${tier === f.id ? ' active' : ''}`}
              onClick={() => setTier(f.id)}>{f.label}</button>
          ))}
        </div>
      </div>
      <div className="rrtv-research-grid">
        {shown.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} className="rrtv-research-card" onClick={() => setOpenId(t.id)}>
              <div className="rrtv-research-card-top">
                <span className="rrtv-research-card-icon"><Icon size={18} /></span>
                <span className={`rrtv-tier-badge ${t.tier}`}>{TIER_LABEL[t.tier]}</span>
              </div>
              <div className="rrtv-research-card-title">{t.title}</div>
              <div className="rrtv-research-card-desc">{t.desc}</div>
              <div className="rrtv-research-card-open">Abrir →</div>
            </button>
          );
        })}
      </div>

      {/* RR editorial acervo — content-rich feed (MagmaNewsSection) */}
      <div className="rrtv-research-news">
        <MagmaNewsSection label="Acervo Editorial" offset={0} />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// CustomPane — build-your-own dashboard: add multiple components,
// responsive grid (stack on mobile), each self-contained in its cell.
// Widget list lives in the tab object (top-level state) → persists.
// ══════════════════════════════════════════════════════════════════
const WIDGET_CATALOG = [
  ...RESEARCH_TOOLS.map(t => ({ id: t.id, title: t.title, tier: t.tier })),
  { id: 'ativo', title: 'Ativo', tier: 'light' },
];

function CustomWidgetBody({ toolId, marketData }) {
  if (toolId === 'ativo') return <AssetWorkbench marketData={marketData} />;
  const tool = RESEARCH_TOOLS.find(t => t.id === toolId);
  if (!tool) return <div className="rrtv-custom-missing">Componente indisponível.</div>;
  const extra = tool.props || {};
  const C = tool.Comp;
  return tool.themed ? <C theme={CHILD_THEME} {...extra} /> : <C {...extra} />;
}

export function CustomPane({ tab, marketData, onChange }) {
  const widgets = tab.widgets || [];
  const seq = tab.seq || 0;
  const [picking, setPicking] = useState(widgets.length === 0);

  const add = (toolId) => {
    onChange({ ...tab, widgets: [...widgets, { uid: `${toolId}-${seq}`, toolId }], seq: seq + 1 });
    setPicking(false);
  };
  const remove = (uid) => onChange({ ...tab, widgets: widgets.filter(w => w.uid !== uid) });
  const setSpan = (uid, span) => onChange({ ...tab, widgets: widgets.map(w => w.uid === uid ? { ...w, span } : w) });

  return (
    <div className="rrtv-custom">
      <div className="rrtv-custom-head">
        <div className="rrtv-workbench-title">
          <h2 className="rrtv-research-h">{tab.name}</h2>
          <p className="rrtv-research-sub">Seu painel. Empilhe os componentes que quiser — monte a tela do seu jeito.</p>
        </div>
        <button className="rrtv-custom-add" onClick={() => setPicking(p => !p)}>
          <Plus size={14} /> Adicionar
        </button>
      </div>

      {picking && (
        <div className="rrtv-custom-picker">
          {WIDGET_CATALOG.map(w => (
            <button key={w.id} className="rrtv-custom-pick" onClick={() => add(w.id)}>
              <span className={`rrtv-tier-badge ${w.tier}`}>{TIER_LABEL[w.tier]}</span>
              <span className="rrtv-custom-pick-t">{w.title}</span>
              <Plus size={13} />
            </button>
          ))}
        </div>
      )}

      {widgets.length === 0 ? (
        <div className="rrtv-custom-empty">
          <LayoutGrid size={26} />
          <p>Painel vazio. Toque em <b>Adicionar</b> para incluir componentes.</p>
        </div>
      ) : (
        <div className="rrtv-custom-grid">
          {widgets.map(w => {
            const meta = WIDGET_CATALOG.find(c => c.id === w.toolId);
            const span = Math.min(w.span || 1, 3);
            return (
              <div key={w.uid} className="rrtv-custom-cell" style={{ gridColumn: `span ${span}` }}>
                <div className="rrtv-custom-cell-head">
                  <span className="rrtv-custom-cell-title">{meta?.title || w.toolId}</span>
                  <div className="rrtv-custom-cell-tools">
                    <div className="rrtv-custom-span" title="Largura (colunas) — desktop">
                      {[1, 2, 3].map(n => (
                        <button key={n} className={`rrtv-custom-span-btn${span === n ? ' active' : ''}`}
                          onClick={() => setSpan(w.uid, n)} title={n === 3 ? 'Linha inteira' : `${n} coluna(s)`}>{n}</button>
                      ))}
                    </div>
                    <button className="rrtv-custom-cell-x" onClick={() => remove(w.uid)} aria-label="Remover"><X size={13} /></button>
                  </div>
                </div>
                <div className="rrtv-custom-cell-body">
                  <CustomWidgetBody toolId={w.toolId} marketData={marketData} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
